<template>
  <div>
    <v-container v-if="!emails && !invitationResults">
      <v-layout wrap row>
        <v-flex xs8>
          <p class="display-1 my-3">Enter or Paste Emails</p>
          <p class="subheading my-3">Enter or paste teacher email addresses separated by lines or commas. Up to 100 emails can be processed at a time, so break larger lists into batches. If you list includes someone already in your school account they will be ignored.</p>
        </v-flex>
        <v-flex xs4 text-xs-center>
            <img src="@/assets/email_list_icon.png" style="width:100%; max-width: 200px"/>
        </v-flex>
      <v-flex xs8>
        <v-textarea
          box
          auto-grow
          v-model="inviteBox"
          label="Email addresses..."
        ></v-textarea>
      </v-flex>
      <v-flex xs4>
          &nbsp;
      </v-flex>
      <v-flex xs4 text-xs-left>
        <v-btn 
          :disabled="validating"
          :to="{ path: '/account/manage-teachers' }">
          Back
        </v-btn>
      </v-flex>
      <v-flex xs4 text-xs-right>
        <v-btn color="secondary" v-on:click="validate" :disabled="!inviteBox" v-if="!validating">
          Validate
        </v-btn>
        <v-progress-circular
          v-if="validating"
          indeterminate
          color="secondary"
        ></v-progress-circular>
      </v-flex>
      </v-layout>
    </v-container>
    <v-container v-if="emails">
      <v-layout wrap row>
        <v-flex xs12 sm10>
          <p class="display-1 my-3">Review</p>
          <p class="subheading my-3">Any issues with your list will be reported below. You can go back and fix issues or ignore them and proceed.</p>
        </v-flex>
        <v-flex xs12 sm10>
          <v-alert
            :value="duplicatesCount"
            type="info"
          >
            Found and removed {{duplicatesCount}} duplicate(s) from your list.
          </v-alert>
          <v-alert
            :value="errorCount"
            type="error"
          >
            Found and highlighted {{errorCount}} error(s).
          </v-alert>
          <v-alert
            :value="limitReached"
            type="warning"
          >
            There is a maximum of 100 emails per batch to improve deliverability.
          </v-alert>
          <v-alert
            :value="errorCount === 0"
            type="success"
            light
          >
            List validated with no errors found.
          </v-alert>
        </v-flex>
        <v-flex xs12 sm10 mt-3>
          <v-list>
            <template v-for="(item, index) in emails">
              <v-list-tile
                :key="item.email"
                :class="item.error ? 'yellow lighten-2' : 'grey lighten-3'"
              >
                <v-list-tile-avatar>
                  {{index + 1}}.
                </v-list-tile-avatar>

                <v-list-tile-content>
                  <v-list-tile-title>{{item.email}}</v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action v-if="item.error" class="font-weight-bold">
                  {{item.error}}
                </v-list-tile-action>
              </v-list-tile>

              <v-divider
                :key="index"
              ></v-divider>
            </template>
          </v-list>
        </v-flex>
        <v-flex xs6 sm5 text-xs-left mt-3>
          <v-btn flat color="primary" v-on:click="emails = null">Back</v-btn>
        </v-flex>
        <v-flex xs6 sm5 text-xs-right mt-3>
          <span class="tiny" v-if="errorCount">Rows with errors will be ignored.</span><v-btn color="secondary" v-on:click="sendInvitations">Invite</v-btn>
        </v-flex>
        <v-flex xs12 sm10 text-xs-center v-if="progress">
            <v-progress-linear height="10" color="secondary" :value="progress"></v-progress-linear>
            <p class="secondary--text">Sending Invitations...</p>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container v-if="invitationResults">
      <v-layout wrap row>
        <v-flex xs12 sm10>
          <p class="display-1 my-3">Invitations Sent</p>
          <p class="subheading my-3">{{ completedCount }} invitation(s) sent. <span v-if="skippedCount > 0">{{ skippedCount }} row(s) was skipped with errors shown below.</span></p>
        </v-flex>
        <v-flex xs12 sm10>
          <v-list>
            <template v-for="(item, index) in invitationResults">
              <v-list-tile
                :key="item.email"
                :class="item.error ? 'yellow lighten-2' : 'grey lighten-5'"
              >
                <v-list-tile-avatar>
                  {{index + 1}}.
                </v-list-tile-avatar>

                <v-list-tile-content>
                  <v-list-tile-title>{{item.email}}</v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action v-if="item.error" class="font-weight-bold">
                  {{item.error}}
                </v-list-tile-action>
                <v-list-tile-action v-if="item.error === null">
                  <div class="secondary--text">Sent</div>
                </v-list-tile-action>
              </v-list-tile>
              <v-divider
                :key="index"
              ></v-divider>
            </template>
          </v-list>
        </v-flex>
        <v-flex xs6 sm5 text-xs-left mt-3>
          &nbsp;
        </v-flex>
        <v-flex xs6 sm5 text-xs-right mt-3>
          <v-btn color="secondary" :to="{ path: '/account/manage-teachers' }">Finish</v-btn>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import _ from 'lodash'
import AuthenticationService from '@/services/AuthenticationService'
import TeacherService from '@/services/TeacherService'

export default {
  data: function () {
    return {
      validating: false,
      inviteBox: null,
      validationErrors: null,
      emails: null,
      invitationResults: null,
      duplicatesCount: null,
      limitReached: false,
      errorCount:null,
      progress:null,
      completedCount: 0,
      skippedCount: 0
    }
  },
  methods: {
    reset() {
      this.inviteBox = null
      this.validationErrors = null
      this.emails = null
      this.invitationResults = null
      this.duplicatesCount = null
      this.progress = null
      this.skippedCount = 0
      this.completedCount = 0
    },
    async validate () {
      this.duplicatesCount = null
      
      this.validating = true
      const convertedString = this.inviteBox.replace(/\n/g, ",");
      const splitList = _.split(convertedString, ',');
      const trimmedList = _.map(splitList, _.trim);
      const filteredList = _.filter(trimmedList, string => {
        return !(string === '')
      })
      let uniqueList = _.uniq(filteredList);

      if (uniqueList.length !== filteredList.length) {
        this.duplicatesCount = filteredList.length - uniqueList.length;
      }

      if (uniqueList.length > 100) {
        this.limitReached = true
        uniqueList = _.slice(uniqueList, 0, 100)
      }


      const results = []
      for (let i = 0; i < uniqueList.length; i++) {
        const email = uniqueList[i]

        try {
          const result = await AuthenticationService.checkUsernameAvailability({username: email});
          
          results.push({ email: email, error: null })
        } catch (err) {
          results.push({ email: email, error: err.response.data.error })
        }
      }

      let errorCount = 0
      this.emails = _.map(results, item => {
        if (item.error) errorCount++
        return item
      })
      this.errorCount = errorCount
      this.validating = false
    },
    async sendInvitations () {
      if (this.emails === null) return;

      let total = this.emails.length
      let errors = 0
      let completed = 0
      this.progress = 0


      const results = []
      for (let i = 0; i < this.emails.length; i++) {
        const val = this.emails[i]

        if (val.error) {
          errors++
          this.progress = ((completed + errors) / total) * 100
          results.push(val)
        } else {
          try {
            await TeacherService.invite({username: val.email});
            completed++
            this.progress = ((completed + errors) / total) * 100
            results.push(val)
          } catch (err) {
            console.log(err.response.data.error)
            errors++
            this.progress = ((completed + errors) / total) * 100
            results.push({email: val.email, error: 'Could not send invitation.' })
          }
        }
      }
      
      this.progress = ((completed + errors) / total) * 100
      
      this.skippedCount = errors
      this.completedCount = completed

      this.emails = null;
      this.invitationResults = results;
    }
  }
}
</script>

<style scoped>
.tiny {
  font-size: 14px;
}

.normal {
  border: 1px solid grey;
}
.box {
  background-color: #f4f4f4;
}

.errtxt {
  color: red;
}

.green--text {
  color: #388E3C!important;
}

.breadcrumb {
  text-transform: uppercase;
  font-weight: 500;
  font-size: 16px;
}
.active {
  border-bottom: 2px #3296f3 solid;
}

</style>
